// src/services/adminService.js
import useAxios from '../utils/useAxios';
import { tenantURL } from '../utils/config';

// ⚠️ Préfixe du module pointage (presences/urls.py) tel que monté dans les
// urls principales. Change UNIQUEMENT cette constante si le préfixe réel
// est différent (ex: 'presences/' au lieu de 'pointage/').
const POINTAGE_PREFIX = 'pointage/';

export const useAdminService = () => {
  const axiosInstance = useAxios();

  // ─── UTILISATEURS ──────────────────────────────────────────────────────
  const getUtilisateurs = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams 
        ? `${tenantURL(slug, 'utilisateurs/')}?${queryParams}`
        : tenantURL(slug, 'utilisateurs/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      throw error;
    }
  };

  const createUtilisateur = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'utilisateurs/create/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      throw error;
    }
  };

  const updateUtilisateur = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `utilisateurs/${id}/update/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', error);
      throw error;
    }
  };

  const deleteUtilisateur = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `utilisateurs/${id}/delete/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      throw error;
    }
  };

  // ─── MON COMPTE ────────────────────────────────────────────────────────
  const getMonCompte = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'mon-profil/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du compte:', error);
      throw error;
    }
  };

  const updateMonCompte = async (slug, data) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, 'mon-profil/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du compte:', error);
      throw error;
    }
  };

  const changerMonMotDePasse = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'mon-profil/mot-de-passe/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      throw error;
    }
  };

  const resetPasswordUtilisateur = async (slug, id, newPassword) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `utilisateurs/${id}/reset-password/`),
        { new_password: newPassword }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      throw error;
    }
  };

const creerUtilisateurAvecInvitation = async (slug, data) => {
  try {
    const response = await axiosInstance.post(tenantURL(slug, 'utilisateurs/creer-invitation/'), data);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création du compte avec invitation :", error);
    throw error;
  }
};  


  // ─── PROFILS ADMINISTRATEUR ────────────────────────────────────────────
  const getProfilsAdministrateurs = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'profils-administrateurs/')}?${queryParams}`
        : tenantURL(slug, 'profils-administrateurs/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des profils administrateurs:', error);
      throw error;
    }
  };

  const createProfilAdministrateur = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'profils-administrateurs/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du profil administrateur:', error);
      throw error;
    }
  };

  const updateProfilAdministrateur = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `profils-administrateurs/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil administrateur:', error);
      throw error;
    }
  };

  const deleteProfilAdministrateur = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `profils-administrateurs/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du profil administrateur:', error);
      throw error;
    }
  };

  const getPermissionsAdmin = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'permissions-admin/')}?${queryParams}`
        : tenantURL(slug, 'permissions-admin/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des permissions:', error);
      throw error;
    }
  };

// ─── IMPORT UTILISATEURS ──────────────────────────────────────────────────
const importUtilisateurs = async (slug, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(tenantURL(slug, 'import/parents/'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'import des utilisateurs:', error);
    throw error;
  }
};


  // ─── ANNÉES SCOLAIRES ────────────────────────────────────────────────
  const getAnneesScolaires = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams 
        ? `${tenantURL(slug, 'annees-scolaires/')}?${queryParams}`
        : tenantURL(slug, 'annees-scolaires/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des années scolaires:', error);
      throw error;
    }
  };

  const createAnneeScolaire = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'annees-scolaires/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'année scolaire:', error);
      throw error;
    }
  };

  const updateAnneeScolaire = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `annees-scolaires/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'année scolaire:', error);
      throw error;
    }
  };

  const deleteAnneeScolaire = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `annees-scolaires/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'année scolaire:', error);
      throw error;
    }
  };

  const setActiveAnneeScolaire = async (slug, id) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `annees-scolaires/${id}/set-active/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'activation de l\'année scolaire:', error);
      throw error;
    }
  };

  const archiveAnneeScolaire = async (slug, id) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `annees-scolaires/${id}/archive/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'archivage de l\'année scolaire:', error);
      throw error;
    }
  };

  const restoreAnneeScolaire = async (slug, id) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `annees-scolaires/${id}/restore/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la restauration de l\'année scolaire:', error);
      throw error;
    }
  };

const deleteClasse = async (slug, classeId) => {
  try {
    await axiosInstance.delete(tenantURL(slug, `classes/${classeId}/`));
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de la classe:', error);
    throw error;
  }
};


  const getAnneeScolaireStats = async (slug, id) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `annees-scolaires/${id}/stats/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
      throw error;
    }
  };


  const genererClassesStandard = async (slug, anneeId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `etablissements/annees-scolaires/${anneeId}/generer-classes/`)
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération des classes standards:', error);
      throw error;
    }
  };
 
  const getSetupStatus = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'etablissements/setup-status/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du statut de configuration:', error);
      throw error;
    }
  };

const genererMatieresStandard = async (slug, anneeId) => {
  try {
    const response = await axiosInstance.post(
      tenantURL(slug, `etablissements/annees-scolaires/${anneeId}/generer-matieres/`)
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la génération des matières standards:', error);
    throw error;
  }
};  


  // ─── CLASSES ────────────────────────────────────────────────────────────
  const getClasses = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams 
        ? `${tenantURL(slug, 'classes/')}?${queryParams}`
        : tenantURL(slug, 'classes/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      throw error;
    }
  };

  const createClasse = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'classes/create/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la classe:', error);
      throw error;
    }
  };

const updateClasse = async (slug, classeId, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `classes/${classeId}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la classe:', error);
      throw error;
    }
  };

  const archiveClasse = async (slug, classeId) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `classes/${classeId}/archive/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'archivage de la classe:', error);
      throw error;
    }
  };

  const restoreClasse = async (slug, classeId) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `classes/${classeId}/restore/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la restauration de la classe:', error);
      throw error;
    }
  };

  const assignEnseignantsClasse = async (slug, classeId, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `classes/${classeId}/assign-enseignants/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'assignation des enseignants:', error);
      throw error;
    }
  };

  const assignMatieresClasse = async (slug, classeId, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `classes/${classeId}/assign-matieres/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'assignation des matières:', error);
      throw error;
    }
  };

// ─── NIVEAUX ───────────────────────────────────────────────────────────
  const getNiveaux = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'etablissements/niveaux/')}?${queryParams}`
        : tenantURL(slug, 'etablissements/niveaux/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des niveaux:', error);
      throw error;
    }
  };

  const createNiveau = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'etablissements/niveaux/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du niveau:', error);
      throw error;
    }
  };





  // ─── FILIÈRES ──────────────────────────────────────────────────────────
  const getFilieres = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'etablissements/filieres/')}?${queryParams}`
        : tenantURL(slug, 'etablissements/filieres/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des filières:', error);
      throw error;
    }
  };

  const createFiliere = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'etablissements/filieres/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la filière:', error);
      throw error;
    }
  };

  const updateFiliere = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `etablissements/filieres/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la filière:', error);
      throw error;
    }
  };


  // ─── PARENTS PAR CLASSE ────────────────────────────────────────────────
  const getParentsParClasse = async (slug, classeId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `classes/${classeId}/parents/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des parents de la classe:', error);
      throw error;
    }
  };

// ─── MATIÈRES ──────────────────────────────────────────────────────────
  const getMatieres = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/matieres/')}?${queryParams}`
        : tenantURL(slug, 'gestion/matieres/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
      throw error;
    }
  };

  const createMatiere = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/matieres/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la matière:', error);
      throw error;
    }
  };

  const updateMatiere = async (slug, id, data) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, `gestion/matieres/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la matière:', error);
      throw error;
    }
  };

  const deleteMatiere = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `gestion/matieres/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la matière:', error);
      throw error;
    }
  };

  const assignMatiereClasses = async (slug, matiere_id, classes_ids) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/matieres/assigner/'), { matiere_id, classes_ids });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'assignation de la matière:', error);
      throw error;
    }
  };


// ─── ENSEIGNEMENTS ─────────────────────────────────────────────────────
  const getEnseignements = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/enseignements/')}?${queryParams}`
        : tenantURL(slug, 'gestion/enseignements/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des enseignements:', error);
      throw error;
    }
  };

  const createEnseignement = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/enseignements/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'enseignement:', error);
      throw error;
    }
  };

  const updateEnseignement = async (slug, id, data) => {
    try {
      // ⚠️ EnseignementDetailAPIView n'a pas de patch — utiliser put
      const response = await axiosInstance.put(tenantURL(slug, `gestion/enseignements/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'enseignement:', error);
      throw error;
    }
  };

  const deleteEnseignement = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `gestion/enseignements/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'enseignement:', error);
      throw error;
    }
  };


  // ─── RÉFÉRENTIELS : MATRICULE / NATIONALITÉS ────────────────────────────
  const getProchainMatricule = async (slug, anneeScolaireId = null) => {
    try {
      const params = anneeScolaireId ? `?annee_scolaire=${anneeScolaireId}` : '';
      const response = await axiosInstance.get(tenantURL(slug, `eleves/prochain-matricule/${params}`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération du matricule:', error);
      throw error;
    }
  };

  const getNationalites = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'nationalites/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des nationalités:', error);
      throw error;
    }
  };

  // ─── ÉTATS SANITAIRES (configurable par établissement) ──────────────────
  const getEtatsSanitaires = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'etats-sanitaires/')}?${queryParams}`
        : tenantURL(slug, 'etats-sanitaires/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des états sanitaires:', error);
      throw error;
    }
  };

  const createEtatSanitaire = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'etats-sanitaires/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'état sanitaire:', error);
      throw error;
    }
  };

  const updateEtatSanitaire = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `etats-sanitaires/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'état sanitaire:', error);
      throw error;
    }
  };

  const deleteEtatSanitaire = async (slug, id) => {
    try {
      const response = await axiosInstance.delete(tenantURL(slug, `etats-sanitaires/${id}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'état sanitaire:', error);
      throw error;
    }
  };

  // ─── SITUATIONS SOCIALES (configurable par établissement) ───────────────
  const getSituationsSociales = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'situations-sociales/')}?${queryParams}`
        : tenantURL(slug, 'situations-sociales/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des situations sociales:', error);
      throw error;
    }
  };

  const createSituationSociale = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'situations-sociales/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la situation sociale:', error);
      throw error;
    }
  };

  const updateSituationSociale = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `situations-sociales/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la situation sociale:', error);
      throw error;
    }
  };

  const deleteSituationSociale = async (slug, id) => {
    try {
      const response = await axiosInstance.delete(tenantURL(slug, `situations-sociales/${id}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la situation sociale:', error);
      throw error;
    }
  };

  // ─── TUTEURS (père / mère) ────────────────────────────────────────────
  const createTuteur = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'tuteurs/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du tuteur:', error);
      throw error;
    }
  };

  const updateTuteur = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `tuteurs/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du tuteur:', error);
      throw error;
    }
  };

  const deleteTuteur = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `tuteurs/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du tuteur:', error);
      throw error;
    }
  };

  const lierCompteTuteur = async (slug, tuteurId, utilisateurId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `tuteurs/${tuteurId}/lier-compte/`),
        { utilisateur: utilisateurId }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la liaison du compte au tuteur:', error);
      throw error;
    }
  };

  const delierCompteTuteur = async (slug, tuteurId) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `tuteurs/${tuteurId}/delier-compte/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la déliaison du compte tuteur:', error);
      throw error;
    }
  };

  // ─── ÉLÈVES ────────────────────────────────────────────────────────────
  const getEleves = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams 
        ? `${tenantURL(slug, 'eleves/')}?${queryParams}`
        : tenantURL(slug, 'eleves/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des élèves:', error);
      throw error;
    }
  };

  const createEleve = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'eleves/create/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'élève:', error);
      throw error;
    }
  };

  const updateEleve = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `eleves/${id}/update/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'élève:', error);
      throw error;
    }
  };

  const deleteEleve = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `eleves/${id}/delete/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'élève:', error);
      throw error;
    }
  };

  const importEleves = async (slug, file, extra = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      Object.entries(extra).forEach(([k, v]) => formData.append(k, v));
      const response = await axiosInstance.post(tenantURL(slug, 'eleves/import/'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'import des élèves:', error);
      throw error;
    }
  };

  
// ─── ÉLÈVE — DÉTAIL COMPLET (parent + abonnement) ───────────────────────
const getEleveDetail = async (slug, id) => {
  try {
    const response = await axiosInstance.get(tenantURL(slug, `eleves/${id}/`));
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement du détail élève:', error);
    throw error;
  }
};


  // ─── INSCRIPTIONS ──────────────────────────────────────────────────────
  const createInscription = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'inscriptions/create/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'inscription:', error);
      throw error;
    }
  };


  const updateInscription = async (slug, id, data) => {
  try {
    const response = await axiosInstance.patch(
      tenantURL(slug, `inscriptions/${id}/update/`),
      data
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de l'inscription:", error);
    throw error;
  }
};


  const deleteInscription = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `inscriptions/${id}/delete/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'inscription:', error);
      throw error;
    }
  };

  // ─── RÉINSCRIPTION EN MASSE (clôture d'année) ───────────────────────────
  const getReinscriptionClasses = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'reinscription/classes/')}?${queryParams}`
        : tenantURL(slug, 'reinscription/classes/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du tableau de bord de réinscription:', error);
      throw error;
    }
  };

  const getReinscriptionElevesClasse = async (slug, classeSourceId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `reinscription/classes/${classeSourceId}/eleves/`)}?${queryParams}`
        : tenantURL(slug, `reinscription/classes/${classeSourceId}/eleves/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des élèves de la classe:', error);
      throw error;
    }
  };

  const validerReinscription = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'reinscription/valider/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la validation de la réinscription en masse:', error);
      throw error;
    }
  };

// ─── TRIMESTRES ────────────────────────────────────────────────────────
  const getTrimestres = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/trimestres/')}?${queryParams}`
        : tenantURL(slug, 'gestion/trimestres/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des trimestres:', error);
      throw error;
    }
  };

  const createTrimestre = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/trimestres/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du trimestre:', error);
      throw error;
    }
  };

  const updateTrimestre = async (slug, id, data) => {
    try {
      // ⚠️ TrimestreDetailAPIView n'a pas de patch — utiliser put
      const response = await axiosInstance.put(tenantURL(slug, `gestion/trimestres/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du trimestre:', error);
      throw error;
    }
  };

  const deleteTrimestre = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `gestion/trimestres/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du trimestre:', error);
      throw error;
    }
  };

  // ─── SOUS-TRIMESTRES ───────────────────────────────────────────────────
  const getSousTrimestres = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/sous-trimestres/')}?${queryParams}`
        : tenantURL(slug, 'gestion/sous-trimestres/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des sous-trimestres:', error);
      throw error;
    }
  };

  const createSousTrimestre = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/sous-trimestres/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du sous-trimestre:', error);
      throw error;
    }
  };

  const updateSousTrimestre = async (slug, id, data) => {
    try {
      // ⚠️ SousTrimestreDetailAPIView n'a pas de patch — utiliser put
      const response = await axiosInstance.put(tenantURL(slug, `gestion/sous-trimestres/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du sous-trimestre:', error);
      throw error;
    }
  };

  const deleteSousTrimestre = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `gestion/sous-trimestres/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du sous-trimestre:', error);
      throw error;
    }
  };

// ─── PLANNIFICATIONS ───────────────────────────────────────────────────
  const getPlannifications = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/plannifications/')}?${queryParams}`
        : tenantURL(slug, 'gestion/plannifications/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des plannifications:', error);
      throw error;
    }
  };

  const createPlannification = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/plannifications/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la plannification:', error);
      throw error;
    }
  };

  const updatePlannification = async (slug, id, data) => {
    try {
      // ⚠️ PlannificationDetailAPIView n'a pas de patch — utiliser put
      const response = await axiosInstance.put(tenantURL(slug, `gestion/plannifications/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la plannification:', error);
      throw error;
    }
  };

  const deletePlannification = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `gestion/plannifications/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la plannification:', error);
      throw error;
    }
  };

  const assignElevesPlannification = async (slug, plannificationId, data) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `gestion/plannifications/${plannificationId}/assign-eleves/`),
        data
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'assignation des élèves:', error);
      throw error;
    }
  };


// ─── NOTES ─────────────────────────────────────────────────────────────
  const getNotes = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/notes/')}?${queryParams}`
        : tenantURL(slug, 'gestion/notes/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des notes:', error);
      throw error;
    }
  };

  const bulkUpdateNotes = async (slug, notes) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'gestion/notes/bulk-update/'), { notes });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des notes:', error);
      throw error;
    }
  };

  const getMatieresParClasse = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'gestion/matieres/classe/')}?${queryParams}`
        : tenantURL(slug, 'gestion/matieres/classe/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des matières de la classe:', error);
      throw error;
    }
  };


// ─── CONFIGURATION BULLETIN ─────────────────────────────────────────────
  const getBulletinConfigurations = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'configurations/bulletins/')}?${queryParams}`
        : tenantURL(slug, '/configurations/bulletins/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des configurations de bulletin:', error);
      throw error;
    }
  };

  // Construit un FormData si un fichier logo est présent, sinon renvoie l'objet tel quel
  const buildBulletinConfigPayload = (data, logoFile) => {
    if (!logoFile) return data;
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (typeof value === "object" && !(value instanceof File)) {
        fd.append(key, JSON.stringify(value));
      } else {
        fd.append(key, value);
      }
    });
    fd.append("logo_pays", logoFile);
    return fd;
  };

  const createBulletinConfiguration = async (slug, data, logoFile = null) => {
    try {
      const payload = buildBulletinConfigPayload(data, logoFile);
      const response = await axiosInstance.post(
        tenantURL(slug, 'configurations/bulletins/'),
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la configuration de bulletin:', error);
      throw error;
    }
  };

  const updateBulletinConfiguration = async (slug, id, data, logoFile = null) => {
    try {
      // ⚠️ .put() ci-dessous car BulletinConfigurationDetailAPIView écrase les
      // champs non fournis avec les valeurs par défaut si "partial" n'est pas géré —
      // on envoie donc l'objet complet. Utilise updateBulletinConfigurationPartial
      // pour une bascule rapide de est_actif uniquement.
      const payload = buildBulletinConfigPayload(data, logoFile);
      const response = await axiosInstance.put(
        tenantURL(slug, `configurations/bulletins/${id}/`),
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la configuration de bulletin:', error);
      throw error;
    }
  };

  const updateBulletinConfigurationPartial = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(
        tenantURL(slug, `configurations/bulletins/${id}/`),
        data
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour partielle de la configuration de bulletin:', error);
      throw error;
    }
  };

  const deleteBulletinConfiguration = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `configurations/bulletins/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la configuration de bulletin:', error);
      throw error;
    }
  };

// ─── ÉTABLISSEMENT COURANT ───────────────────────────────────────────────
  const getEtablissementCourant = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'etablissements/etablissement/'));
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de l'établissement:", error);
      throw error;
    }
  };




// ─── BULLETINS ─────────────────────────────────────────────────────────
  const genererBulletinsParPlannification = async (slug, plannificationId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'gestion/bulletins/generer-par-plannification/'),
        { plannification_id: plannificationId }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération des bulletins:', error);
      throw error;
    }
  };

  const genererBulletinsParEleves = async (slug, elevesIds, plannificationId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'gestion/bulletins/generer-par-eleves/'),
        { eleves: elevesIds, plannification_id: plannificationId }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération des bulletins (élèves):', error);
      throw error;
    }
  };

  const getStatutEnvoiBulletins = async (slug, plannificationId) => {
    try {
      const response = await axiosInstance.get(
        tenantURL(slug, `gestion/bulletins/statut-envoi/${plannificationId}/`)
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du statut des bulletins:', error);
      throw error;
    }
  };

  const reinitialiserStatutEnvoi = async (slug, plannificationId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `gestion/bulletins/reinitialiser-envoi/${plannificationId}/`)
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la réinitialisation du statut d'envoi:", error);
      throw error;
    }
  };

  const publierBulletins = async (slug, plannificationId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'gestion/bulletins/publier/'),
        { plannification_id: plannificationId }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la publication des bulletins:', error);
      throw error;
    }
  };

  const envoyerBulletinsParentsEmail = async (slug, data) => {
    // data: { bulletins: [ids] }  OU  { plannification_id }
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'gestion/bulletins/envoyer-parents/'),
        data
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi des bulletins par email:", error);
      throw error;
    }
  };

  const envoyerBulletinIndividuelEmail = async (slug, bulletinId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `gestion/bulletins/${bulletinId}/envoyer-parent/`)
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi individuel du bulletin par email:", error);
      throw error;
    }
  };

  const envoyerBulletinsParentsWhatsapp = async (slug, data) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'gestion/bulletins/envoyer-parents-whatsapp/'),
        data
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi des bulletins par WhatsApp:", error);
      throw error;
    }
  };

  const envoyerBulletinIndividuelWhatsapp = async (slug, bulletinId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `gestion/bulletins/${bulletinId}/envoyer-parent-whatsapp/`)
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi individuel du bulletin par WhatsApp:", error);
      throw error;
    }
  };

const getBulletinDetail = async (slug, bulletinId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `gestion/bulletins/${bulletinId}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du bulletin:', error);
      throw error;
    }
  };

const telechargerBulletinsPDF = async (slug, payload) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'gestion/bulletins/telecharger-pdf/'),
        payload,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du téléchargement des bulletins PDF:', error);
      throw error;
    }
  };

// ─── CONFIGURATION CARTE SCOLAIRE ────────────────────────────────────────
  const getConfigurationsCarte = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'configurations/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des configurations de carte:', error);
      throw error;
    }
  };

  const createConfigurationCarte = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'configurations/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la configuration de carte:', error);
      throw error;
    }
  };

  const updateConfigurationCarte = async (slug, id, data) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, `configurations/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la configuration de carte:', error);
      throw error;
    }
  };

  const updateConfigurationCartePartial = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `configurations/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour partielle de la configuration de carte:', error);
      throw error;
    }
  };

  const deleteConfigurationCarte = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `configurations/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la configuration de carte:', error);
      throw error;
    }
  };

// ─── CARTES SCOLAIRES ────────────────────────────────────────────────────
  const genererCartesParClasse = async (slug, classeId, anneeScolaireId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'cartes/generer-par-classe/'),
        { classe_id: classeId, annee_scolaire: anneeScolaireId }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération des cartes (classe):', error);
      throw error;
    }
  };

  const genererCartesParEleves = async (slug, elevesIds, anneeScolaireId) => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, 'cartes/generer-par-eleves/'),
        { eleves: elevesIds, annee_scolaire: anneeScolaireId }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération des cartes (élèves):', error);
      throw error;
    }
  };
const telechargerCartesPDF = async (slug, payload) => {
  try {
    const response = await axiosInstance.post(
      tenantURL(slug, 'cartes/telecharger-pdf/'),
      payload,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors du téléchargement des cartes PDF:', error);
    throw error;
  }
};

  // ─── PARAMÈTRES ────────────────────────────────────────────────────────
  const getParametres = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'parametres/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      throw error;
    }
  };

  const updateParametres = async (slug, data) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, 'parametres/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      throw error;
    }
  };

  // ─── DASHBOARD ────────────────────────────────────────────────────────
  const getDashboardStats = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'etablissements/dashboard/stats/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
      throw error;
    }
  };

  const getPresenceDetails = async (slug, date = null) => {
    try {
      const url = date 
        ? tenantURL(slug, `dashboard/presences/${date}/`)
        : tenantURL(slug, 'dashboard/presences/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des présences:', error);
      throw error;
    }
  };

  // ─── PARENTS ──────────────────────────────────────────────────────────
  const getParents = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams 
        ? `${tenantURL(slug, 'parents/details/')}?${queryParams}`
        : tenantURL(slug, 'parents/details/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des parents:', error);
      throw error;
    }
  };

  const getParentDetail = async (slug, id) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `parents/${id}/details/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du parent:', error);
      throw error;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // ─── POINTAGE : PRÉSENCES ────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════

  const getPresencesClasse = async (slug, classeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}presences/${classeId}/`)}?${queryParams}`
        : tenantURL(slug, `${POINTAGE_PREFIX}presences/${classeId}/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des présences:', error);
      throw error;
    }
  };

  const getPresencesHistorique = async (slug, classeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}presences/${classeId}/historique/`)}?${queryParams}`
        : tenantURL(slug, `${POINTAGE_PREFIX}presences/${classeId}/historique/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de l'historique des présences:", error);
      throw error;
    }
  };

  const getElevePresences = async (slug, eleveId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `${POINTAGE_PREFIX}eleve/${eleveId}/presences/`));
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement des présences de l'élève:", error);
      throw error;
    }
  };

  const getEleveStatsMensuelles = async (slug, eleveId, year, month) => {
    try {
      const response = await axiosInstance.get(
        tenantURL(slug, `${POINTAGE_PREFIX}eleve/${eleveId}/stats/${year}/${month}/`)
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques mensuelles:', error);
      throw error;
    }
  };

  const modifierPresence = async (slug, pk, data) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, `${POINTAGE_PREFIX}modifier-presence/${pk}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification de la présence:', error);
      throw error;
    }
  };

  const ajouterPresenceEleve = async (slug, data) => {
    // data: { matricule, date, heure, type_pointage, periode, motif_sortie }
    try {
      const response = await axiosInstance.post(tenantURL(slug, `${POINTAGE_PREFIX}pointage-eleve-ajouter/`), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'ajout de la présence:", error);
      throw error;
    }
  };

  const enregistrerPresencesRapide = async (slug, data) => {
    // data: { presences: [{matricule, heure}], classe_id, date, periode }
    try {
      const response = await axiosInstance.post(tenantURL(slug, `${POINTAGE_PREFIX}enregistrer-presences-rapide/`), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'enregistrement rapide des présences:", error);
      throw error;
    }
  };

  const getElevesNonPointes = async (slug, classeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}eleves-non-pointes/${classeId}/`)}?${queryParams}`
        : tenantURL(slug, `${POINTAGE_PREFIX}eleves-non-pointes/${classeId}/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des élèves non pointés:', error);
      throw error;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // ─── POINTAGE : JUSTIFICATIONS D'ABSENCE ─────────────────────────────
  // ═══════════════════════════════════════════════════════════════════

  const getJustifications = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}justifications/`)}?${queryParams}`
        : tenantURL(slug, `${POINTAGE_PREFIX}justifications/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des justifications:', error);
      throw error;
    }
  };

  const creerJustification = async (slug, data, pieceJointeFile = null) => {
    try {
      let payload = data;
      if (pieceJointeFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('piece_jointe', pieceJointeFile);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, `${POINTAGE_PREFIX}justifications/`), payload);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la justification:', error);
      throw error;
    }
  };

  const getJustificationDetail = async (slug, pk) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `${POINTAGE_PREFIX}justifications/${pk}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la justification:', error);
      throw error;
    }
  };

  const updateJustification = async (slug, pk, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `${POINTAGE_PREFIX}justifications/${pk}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la justification:', error);
      throw error;
    }
  };

  const deleteJustification = async (slug, pk) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `${POINTAGE_PREFIX}justifications/${pk}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la justification:', error);
      throw error;
    }
  };

  const approuverJustification = async (slug, pk) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `${POINTAGE_PREFIX}justifications/${pk}/approuver/`));
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'approbation de la justification:", error);
      throw error;
    }
  };

  const rejeterJustification = async (slug, pk, commentaire = '') => {
    try {
      const response = await axiosInstance.post(
        tenantURL(slug, `${POINTAGE_PREFIX}justifications/${pk}/rejeter/`),
        { commentaire }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du rejet de la justification:', error);
      throw error;
    }
  };

  const getJustificationsStats = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `${POINTAGE_PREFIX}justifications/stats/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques de justifications:', error);
      throw error;
    }
  };

// ─── MESSAGERIE ────────────────────────────────────────────────────────
  const getMessagesNonLusCount = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'message/messages/non-lus/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du nombre de messages non lus:', error);
      throw error;
    }
  };

  const getMessagesRecus = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'message/messages-recus/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des conversations:', error);
      throw error;
    }
  };

  const getMessagerieParent = async (slug, parentId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `message/messagerie/parent/${parentId}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la conversation avec le parent:', error);
      throw error;
    }
  };

  const postMessagerieParent = async (slug, parentId, data, pieceJointeFile = null) => {
    try {
      let payload = data;
      if (pieceJointeFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('piece_jointe', pieceJointeFile);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, `message/messagerie/parent/${parentId}/`), payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi du message au parent:", error);
      throw error;
    }
  };

  const getMessagerieAdmin = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'message/messagerie/admin/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la messagerie interne:', error);
      throw error;
    }
  };

  const postMessagerieAdmin = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'message/messagerie/admin/'), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi du message à l'équipe:", error);
      throw error;
    }
  };

  const getNouveauMessageData = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'message/nouveau-message/')}?${queryParams}`
        : tenantURL(slug, 'message/nouveau-message/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des données de nouveau message:', error);
      throw error;
    }
  };


// ─── NOTIFICATIONS ─────────────────────────────────────────────────────
  const getNotifications = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'notification/notifications/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      throw error;
    }
  };

  const createNotification = async (slug, data, fichier = null) => {
    try {
      let payload = data;
      if (fichier) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (Array.isArray(value)) value.forEach(v => fd.append(key, v));
          else fd.append(key, value);
        });
        fd.append('fichier', fichier);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, 'notification/notifications/create/'), payload);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error);
      throw error;
    }
  };

  const getNotificationDetail = async (slug, id) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `notification/notifications/${id}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la notification:', error);
      throw error;
    }
  };

  const deleteNotification = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `notification/notifications/${id}/delete/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
      throw error;
    }
  };

  const getNotificationsRecues = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'notification/notifications-recues/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des notifications reçues:', error);
      throw error;
    }
  };

  const getNotificationsNonLuesCount = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'notification/notifications/non-lues/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du nombre de notifications non lues:', error);
      throw error;
    }
  };

  const notifierAbsences = async (slug, data = {}) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'notification/notifier-absences/'), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi des notifications d'absences:", error);
      throw error;
    }
  };

  // ─── NOTES D'INFORMATION ────────────────────────────────────────────────
  const getNotesInformation = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'notification/notes/')}?${queryParams}`
        : tenantURL(slug, 'notification/notes/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement des notes d'information:", error);
      throw error;
    }
  };

  const createNoteInformation = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'notification/notes/'), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de la note d'information:", error);
      throw error;
    }
  };

  const updateNoteInformation = async (slug, id, data) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, `notification/notes/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la note d'information:", error);
      throw error;
    }
  };

  const deleteNoteInformation = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `notification/notes/${id}/`));
      return true;
    } catch (error) {
      console.error("Erreur lors de la suppression de la note d'information:", error);
      throw error;
    }
  };


// ─── NOTES ─────────────────────────────────────────────────────────────
const importerNotesExcel = async (slug, formData) => {
  try {
    const response = await axiosInstance.post(
      tenantURL(slug, 'gestion/notes/import-excel/'),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'import des notes:', error);
    throw error;
  }
};



const updateNiveau = async (slug, id, data) => {
  try {
    const response = await axiosInstance.patch(tenantURL(slug, `etablissements/niveaux/${id}/`), data);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du niveau:', error);
    throw error;
  }
};

const deleteNiveau = async (slug, id) => {
  try {
    await axiosInstance.delete(tenantURL(slug, `etablissements/niveaux/${id}/`));
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression du niveau:', error);
    throw error;
  }
};


// ─── ÉTABLISSEMENT ────────────────────────────────────────────────────────
const updateEtablissement = async (slug, data) => {
  try {
    const response = await axiosInstance.put(
      tenantURL(slug, 'etablissements/etablissement/'),
      data,
      { headers: { 'Content-Type': undefined } } // force axios à recalculer le boundary multipart
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'établissement:', error.response?.data || error);
    throw error;
  }
};

const deleteFiliere = async (slug, id) => {
  try {
    await axiosInstance.delete(tenantURL(slug, `etablissements/filieres/${id}/`));
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de la filière:', error);
    throw error;
  }
};


// ═══════════════════════════════════════════════════════════════════
// ─── ABONNEMENTS ──────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

const getElevesAbonnables = async (slug, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${tenantURL(slug, 'eleves-abonnables/')}?${queryParams}`
      : tenantURL(slug, 'eleves-abonnables/');
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des élèves:', error);
    throw error;
  }
};

const getAbonnementsEtablissement = async (slug, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${tenantURL(slug, 'abonnements/')}?${queryParams}`
      : tenantURL(slug, 'abonnements/');
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des abonnements:', error);
    throw error;
  }
};

const assignerOffreEleve = async (slug, eleveId, offreId) => {
  try {
    const response = await axiosInstance.post(tenantURL(slug, 'abonnements/assigner/'), {
      eleve_id: eleveId,
      offre_id: offreId,
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'attribution de l'offre:", error);
    throw error;
  }
};

const renouvelerAbonnement = async (slug, abonnementId, data) => {
  try {
    const response = await axiosInstance.post(
      tenantURL(slug, `abonnements/${abonnementId}/renouveler/`), data
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors du renouvellement:', error);
    throw error;
  }
};

const resilierAbonnement = async (slug, abonnementId) => {
  try {
    const response = await axiosInstance.post(tenantURL(slug, `abonnements/${abonnementId}/resilier/`));
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la résiliation de l'abonnement:", error);
    throw error;
  }
};

const getHistoriqueAbonnements = async (slug, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${tenantURL(slug, 'historique-abonnements/')}?${queryParams}`
      : tenantURL(slug, 'historique-abonnements/');
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement de l'historique:", error);
    throw error;
  }
};

const getOffresPubliques = async () => {
  try {
    const response = await axiosInstance.get('api/offres/');
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des offres:', error);
    throw error;
  }
};

const getFinancesAbonnements = async (slug, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${tenantURL(slug, 'finances-abonnements/')}?${queryParams}`
      : tenantURL(slug, 'finances-abonnements/');
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des statistiques financières:', error);
    throw error;
  }
};



  // ═══════════════════════════════════════════════════════════════
  // ─── CONFIGURATION : TYPES DE FRAIS ──────────────────────────────
  // ═══════════════════════════════════════════════════════════════
 
  const getTypesFrais = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'caisse/types-frais/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des types de frais:', error);
      throw error;
    }
  };
 
  const createTypeFrais = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/types-frais/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du type de frais:', error);
      throw error;
    }
  };
 
  const updateTypeFrais = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `caisse/types-frais/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du type de frais:', error);
      throw error;
    }
  };
 
  const deleteTypeFrais = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `caisse/types-frais/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du type de frais:', error);
      throw error;
    }
  };
 
  // ═══════════════════════════════════════════════════════════════
  // ─── CONFIGURATION : TYPES D'ÉLÈVES ──────────────────────────────
  // ═══════════════════════════════════════════════════════════════
 
  const getTypesEleves = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'caisse/types-eleves/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des types d\'élèves:', error);
      throw error;
    }
  };
 
  const createTypeEleve = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/types-eleves/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du type d\'élève:', error);
      throw error;
    }
  };
 
  const updateTypeEleve = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `caisse/types-eleves/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du type d\'élève:', error);
      throw error;
    }
  };
 
  const deleteTypeEleve = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `caisse/types-eleves/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du type d\'élève:', error);
      throw error;
    }
  };
 
  // ═══════════════════════════════════════════════════════════════
  // ─── CONFIGURATION : TRANCHES DE PAIEMENT ────────────────────────
  // ═══════════════════════════════════════════════════════════════
 
  const getTranches = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'caisse/tranches/')}?${queryParams}`
        : tenantURL(slug, 'caisse/tranches/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des tranches:', error);
      throw error;
    }
  };
 
  const createTranche = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/tranches/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la tranche:', error);
      throw error;
    }
  };
 
  const updateTranche = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `caisse/tranches/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la tranche:', error);
      throw error;
    }
  };
 
  const deleteTranche = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `caisse/tranches/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la tranche:', error);
      throw error;
    }
  };
 
  // ═══════════════════════════════════════════════════════════════
  // ─── CONFIGURATION : AFFECTATIONS DE FRAIS (classe ↔ tranche) ────
  // ═══════════════════════════════════════════════════════════════
 
  const getAffectationsFrais = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'caisse/affectations/')}?${queryParams}`
        : tenantURL(slug, 'caisse/affectations/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des affectations de frais:', error);
      throw error;
    }
  };
 
  const createAffectationFrais = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/affectations/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'affectation de frais:', error);
      throw error;
    }
  };
 
  const updateAffectationFrais = async (slug, id, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `caisse/affectations/${id}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'affectation de frais:', error);
      throw error;
    }
  };
 
  const deleteAffectationFrais = async (slug, id) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `caisse/affectations/${id}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'affectation de frais:', error);
      throw error;
    }
  };

// data: { classe, affectations: [{ tranche, type_eleve, montant }, ...] }
const bulkAffectationsFrais = async (slug, data) => {
  try {
    const response = await axiosInstance.post(tenantURL(slug, 'caisse/affectations/bulk/'), data);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement groupé des affectations de frais:', error);
    throw error;
  }
};  



  // ═══════════════════════════════════════════════════════════════
  // ─── SITUATION ÉLÈVE / VERSEMENTS / REMISES ──────────────────────
  // ═══════════════════════════════════════════════════════════════
 
  const getSituationFrais = async (slug, inscriptionId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `caisse/eleves/${inscriptionId}/situation/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la situation financière:', error);
      throw error;
    }
  };
 
  // data: { type_frais, caisse, montant, mode_paiement, engagement?, rubrique?, imputations?: [{engagement_id, montant}] }
  const creerVersement = async (slug, inscriptionId, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `caisse/eleves/${inscriptionId}/versements/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du versement:', error);
      throw error;
    }
  };
 
  const getRemisesEleve = async (slug, inscriptionId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `caisse/eleves/${inscriptionId}/remises/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des remises:', error);
      throw error;
    }
  };
 
  const creerRemiseIndividuelle = async (slug, inscriptionId, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `caisse/eleves/${inscriptionId}/remises/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la remise:', error);
      throw error;
    }
  };
 
  const annulerVersement = async (slug, id, motif) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `caisse/versements/${id}/annuler/`), { motif });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'annulation du versement:', error);
      throw error;
    }
  };
 
  // Retourne { blob, nombreImpressions } — blob à ouvrir avec URL.createObjectURL,
  // nombreImpressions vient de l'en-tête X-Nombre-Impressions renvoyé par le
  // backend (permet d'avertir le caissier en cas de réimpression, cf. VersementRow).
  const getVersementRecuPDF = async (slug, id) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `caisse/versements/${id}/recu/`), {
        responseType: 'blob',
      });
      return {
        blob: response.data,
        nombreImpressions: Number(response.headers['x-nombre-impressions'] || 1),
      };
    } catch (error) {
      console.error('Erreur lors du téléchargement du reçu:', error);
      throw error;
    }
  };



 
  // ═══════════════════════════════════════════════════════════════
  // ─── CAISSES / MOUVEMENTS / DÉCAISSEMENT / TRANSFERT ─────────────
  // ═══════════════════════════════════════════════════════════════
 
  const getCaisses = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'caisse/caisses/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des caisses:', error);
      throw error;
    }
  };
 
  const createCaisse = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/caisses/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la caisse:', error);
      throw error;
    }
  };
 
  const getCaisseDetail = async (slug, caisseId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `caisse/caisses/${caisseId}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la caisse:', error);
      throw error;
    }
  };
 
  const updateCaisse = async (slug, caisseId, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, `caisse/caisses/${caisseId}/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la caisse:', error);
      throw error;
    }
  };
 
  const getMouvementsCaisse = async (slug, caisseId, params = {}) => {
    // params: { date_debut, date_fin }
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `caisse/caisses/${caisseId}/mouvements/`)}?${queryParams}`
        : tenantURL(slug, `caisse/caisses/${caisseId}/mouvements/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des mouvements:', error);
      throw error;
    }
  };
 
  // data: { rubrique, montant, libelle, date_comptable?, piece_justificative? } (FormData si fichier)
  const creerDecaissement = async (slug, caisseId, data, pieceJustificativeFile = null) => {
    try {
      let payload = data;
      if (pieceJustificativeFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('piece_justificative', pieceJustificativeFile);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, `caisse/caisses/${caisseId}/decaissement/`), payload);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du décaissement:', error);
      throw error;
    }
  };

  // data: { rubrique, montant, libelle, date_comptable?, piece_justificative? } (FormData si fichier)
  const creerEncaissement = async (slug, caisseId, data, pieceJustificativeFile = null) => {
    try {
      let payload = data;
      if (pieceJustificativeFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('piece_justificative', pieceJustificativeFile);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, `caisse/caisses/${caisseId}/encaissement/`), payload);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'encaissement:', error);
      throw error;
    }
  };  

  // data: { caisse_source, caisse_destination, montant, libelle, rubrique_sortie, rubrique_entree }
  const creerTransfert = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/transferts/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du transfert entre caisses:', error);
      throw error;
    }
  };
 
  const getRubriques = async (slug, params = {}) => {
    // params: { type_mouvement: 'RECETTE' | 'DEPENSE' }
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'caisse/rubriques/')}?${queryParams}`
        : tenantURL(slug, 'caisse/rubriques/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des rubriques:', error);
      throw error;
    }
  };
 
  const createRubrique = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/rubriques/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la rubrique:', error);
      throw error;
    }
  };
 
  const getModesPaiement = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'caisse/modes-paiement/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des modes de paiement:', error);
      throw error;
    }
  };
 
  const createModePaiement = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/modes-paiement/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du mode de paiement:', error);
      throw error;
    }
  };
 
  // ═══════════════════════════════════════════════════════════════
  // ─── SESSIONS DE CAISSE / CLÔTURES / AFFECTATIONS UTILISATEUR ────
  // ═══════════════════════════════════════════════════════════════
 
  const ouvrirSessionCaisse = async (slug, caisseId, soldeOuvertureCompte = null) => {
    try {
      const data = soldeOuvertureCompte !== null ? { solde_ouverture_compte: soldeOuvertureCompte } : {};
      const response = await axiosInstance.post(tenantURL(slug, `caisse/caisses/${caisseId}/sessions/ouvrir/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'ouverture de la session de caisse:', error);
      throw error;
    }
  };
 
  const fermerSessionCaisse = async (slug, caisseId, soldeFermetureCompte = null) => {
    try {
      const data = soldeFermetureCompte !== null ? { solde_fermeture_compte: soldeFermetureCompte } : {};
      const response = await axiosInstance.post(tenantURL(slug, `caisse/caisses/${caisseId}/sessions/fermer/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la fermeture de la session de caisse:', error);
      throw error;
    }
  };
 
  const getCloturesCaisse = async (slug, caisseId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `caisse/caisses/${caisseId}/clotures/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des clôtures:', error);
      throw error;
    }
  };
 
  // data: { date_cloture, solde_compte, observations? }
  const creerClotureCaisse = async (slug, caisseId, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `caisse/caisses/${caisseId}/clotures/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la clôture:', error);
      throw error;
    }
  };
 
  const validerClotureCaisse = async (slug, id) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, `caisse/clotures/${id}/valider/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la validation de la clôture:', error);
      throw error;
    }
  };
 
  const getCaisseUtilisateurs = async (slug, caisseId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `caisse/caisses/${caisseId}/utilisateurs/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs affectés à la caisse:', error);
      throw error;
    }
  };
 
  const affecterUtilisateurCaisse = async (slug, caisseId, data) => {
    // data: { utilisateur, ... }
    try {
      const response = await axiosInstance.post(tenantURL(slug, `caisse/caisses/${caisseId}/utilisateurs/`), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'affectation de l\'utilisateur à la caisse:', error);
      throw error;
    }
  };
 
// ═══════════════════════════════════════════════════════════════
// ─── BILAN GLOBAL ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════

const getBilanCaisse = async (slug, params = {}) => {
  // params: { annee_scolaire?, date_debut?, date_fin? }
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `${tenantURL(slug, 'caisse/bilan/')}?${queryParams}`
      : tenantURL(slug, 'caisse/bilan/');
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement du bilan de caisse:', error);
    throw error;
  }
};






  // ═══════════════════════════════════════════════════════════════
  // ─── CONVOCATIONS DE FRAIS ────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════
 
  // data: { classe_id?, eleve_ids?: [], filtre_non_a_jour?: bool, modele_texte }
  const genererConvocations = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'caisse/convocations/generer/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la génération des convocations:', error);
      throw error;
    }
  };

// ── Versements : historique ──────────────────────────────────
const getVersementsHistorique = async (slug, inscriptionId) => {
  try {
    const response = await axiosInstance.get(
      tenantURL(slug, `caisse/eleves/${inscriptionId}/versements/`)
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement de l'historique des versements:", error);
    throw error;
  }
};

// ── Rubriques : update/delete ────────────────────────────────
const updateRubrique = async (slug, id, payload) => {
  try {
    const response = await axiosInstance.put(
      tenantURL(slug, `caisse/rubriques/${id}/`),
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la rubrique:', error);
    throw error;
  }
};

const deleteRubrique = async (slug, id) => {
  try {
    await axiosInstance.delete(tenantURL(slug, `caisse/rubriques/${id}/`));
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de la rubrique:', error);
    throw error;
  }
};

// ── Modes de paiement : update/delete ────────────────────────
const updateModePaiement = async (slug, id, payload) => {
  try {
    const response = await axiosInstance.put(
      tenantURL(slug, `caisse/modes-paiement/${id}/`),
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du mode de paiement:', error);
    throw error;
  }
};

const deleteModePaiement = async (slug, id) => {
  try {
    await axiosInstance.delete(tenantURL(slug, `caisse/modes-paiement/${id}/`));
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression du mode de paiement:', error);
    throw error;
  }
};

  return {
    // Utilisateurs
    getUtilisateurs,
    createUtilisateur,
    updateUtilisateur,
    deleteUtilisateur,
    resetPasswordUtilisateur,
    creerUtilisateurAvecInvitation,
    getMonCompte, updateMonCompte, changerMonMotDePasse,
importUtilisateurs,
  // ─── PROFILS ADMINISTRATEUR ────────────────────────────────────────────
  getProfilsAdministrateurs,
  createProfilAdministrateur,
  updateProfilAdministrateur,
  deleteProfilAdministrateur,
  getPermissionsAdmin,
    // Années scolaires
    getAnneesScolaires,
    createAnneeScolaire,
    updateAnneeScolaire,
    deleteAnneeScolaire,
    setActiveAnneeScolaire,
    archiveAnneeScolaire,
    restoreAnneeScolaire,
    getAnneeScolaireStats,
  genererClassesStandard,  
  getSetupStatus,     
  genererMatieresStandard ,
    // Classes
    getClasses,
    createClasse,
    updateClasse ,
    archiveClasse,
    restoreClasse,
    deleteClasse,
    assignEnseignantsClasse,
    assignMatieresClasse,
// NIVEAUX
getNiveaux,
createNiveau,
updateNiveau,
deleteNiveau,

// FILIÈRES
getFilieres,
createFiliere,
updateFiliere,
deleteFiliere,

// PARENTS PAR CLASSE
getParentsParClasse,

// MATIÈRES
getMatieres,
createMatiere,
updateMatiere,
deleteMatiere,
assignMatiereClasses,


// ENSEIGNEMENTS
getEnseignements,
createEnseignement,
updateEnseignement,
deleteEnseignement,

    getProchainMatricule,
    getNationalites,
    getEtatsSanitaires, createEtatSanitaire, updateEtatSanitaire, deleteEtatSanitaire,
    getSituationsSociales, createSituationSociale, updateSituationSociale, deleteSituationSociale,
    createTuteur, updateTuteur, deleteTuteur, lierCompteTuteur, delierCompteTuteur,

    // Élèves
    getEleves,
    createEleve,
    updateEleve,
    deleteEleve,
    importEleves,
    getEleveDetail,
    // Inscriptions
    createInscription,
    updateInscription,
    deleteInscription,

    getReinscriptionClasses,
    getReinscriptionElevesClasse,
    validerReinscription,    

    // Parents
    getParents,
    getParentDetail,
    

// Trimestres
getTrimestres,
createTrimestre,
updateTrimestre,
deleteTrimestre,

// Sous-trimestres
getSousTrimestres,
createSousTrimestre,
updateSousTrimestre,
deleteSousTrimestre,


// Plannifications
getPlannifications,
createPlannification,
updatePlannification,
deletePlannification,
assignElevesPlannification,

// Notes
getNotes,
bulkUpdateNotes,
getMatieresParClasse,



// CONFIGURATION BULLETIN
getBulletinConfigurations,
buildBulletinConfigPayload,
createBulletinConfiguration,
updateBulletinConfiguration,
updateBulletinConfigurationPartial,
deleteBulletinConfiguration,



// BULLETINS
genererBulletinsParPlannification,
genererBulletinsParEleves,
getStatutEnvoiBulletins,
reinitialiserStatutEnvoi,
publierBulletins,
envoyerBulletinsParentsEmail,
envoyerBulletinIndividuelEmail,
envoyerBulletinsParentsWhatsapp,
envoyerBulletinIndividuelWhatsapp,

getEtablissementCourant,

getBulletinDetail,
telechargerBulletinsPDF,
// CONFIGURATION CARTE SCOLAIRE
getConfigurationsCarte,
createConfigurationCarte,
updateConfigurationCarte,
updateConfigurationCartePartial,
deleteConfigurationCarte,


genererCartesParClasse, genererCartesParEleves,
telechargerCartesPDF,

// Messagerie
    getMessagesNonLusCount,
    getMessagesRecus,
    getMessagerieParent,
    postMessagerieParent,
    getMessagerieAdmin,
    postMessagerieAdmin,
    getNouveauMessageData,


    // Paramètres
    getParametres,
    updateParametres,
    
    // Dashboard
    getDashboardStats,
    getPresenceDetails,

    // POINTAGE — Présences
    getPresencesClasse,
    getPresencesHistorique,
    getElevePresences,
    getEleveStatsMensuelles,
    modifierPresence,
    ajouterPresenceEleve,
    enregistrerPresencesRapide,
    getElevesNonPointes,

    // POINTAGE — Justifications
    getJustifications,
    creerJustification,
    getJustificationDetail,
    updateJustification,
    deleteJustification,
    approuverJustification,
    rejeterJustification,
    getJustificationsStats,

// Notifications
    getNotifications,
    createNotification,
    getNotificationDetail,
    deleteNotification,
    getNotificationsRecues,
    getNotificationsNonLuesCount,
    notifierAbsences,

    // Notes d'information
    getNotesInformation,
    createNoteInformation,
    updateNoteInformation,
    deleteNoteInformation,
importerNotesExcel,


updateEtablissement,


  getElevesAbonnables,
  getAbonnementsEtablissement,
  assignerOffreEleve,
  renouvelerAbonnement,
  resilierAbonnement,
  getHistoriqueAbonnements,
  getOffresPubliques,
getFinancesAbonnements,


   // Types de frais
    getTypesFrais,
    createTypeFrais,
    updateTypeFrais,
    deleteTypeFrais,
 
    // Types d'élèves
    getTypesEleves,
    createTypeEleve,
    updateTypeEleve,
    deleteTypeEleve,
 
    // Tranches de paiement
    getTranches,
    createTranche,
    updateTranche,
    deleteTranche,
 
    // Affectations de frais
    getAffectationsFrais,
    createAffectationFrais,
    updateAffectationFrais,
    deleteAffectationFrais,
    bulkAffectationsFrais,

    // Situation / versements / remises
    getSituationFrais,
    creerVersement,
    getRemisesEleve,
    creerRemiseIndividuelle,
    annulerVersement,
    getVersementRecuPDF,
 
    // Caisses / mouvements / décaissement / transfert
    getCaisses,
    createCaisse,
    getCaisseDetail,
    updateCaisse,
    getMouvementsCaisse,
    creerDecaissement,
    creerEncaissement,
    creerTransfert,
    getRubriques,
    createRubrique,
    getModesPaiement,
    createModePaiement,
 
    // Sessions / clôtures / utilisateurs affectés
    ouvrirSessionCaisse,
    fermerSessionCaisse,
    getCloturesCaisse,
    creerClotureCaisse,
    validerClotureCaisse,
    getCaisseUtilisateurs,
    affecterUtilisateurCaisse,
 getBilanCaisse,
    // Convocations
    genererConvocations,


getVersementsHistorique,
updateRubrique,
deleteRubrique,
updateModePaiement,
deleteModePaiement,




  };
};